// src/api/authService.js
import { getCookieValue } from '../api/cookie';

const API_URL = process.env.REACT_APP_API_URL || "";

// Custom error class to handle API errors consistently
export class ApiError extends Error {
  constructor(message, statusCode) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

/**
 * A small helper for POSTing JSON to the server.
 * Throws a standardized ApiError if response is not OK.
 */
async function postJson(endpoint, payload) {
  const csrfToken = getCookieValue('csrf_token');
  
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new ApiError(
        data.error || data.description || 'Request failed', 
        response.status
      );
    }
    
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error. Please try again later.', 0);
  }
}

/** Lightweight helper for GET requests with CSRF */
async function getJson(endpoint) {
  const csrfToken = getCookieValue('csrf_token');
  
  try {
    const res = await fetch(`${API_URL}${endpoint}`, {
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfToken
      }
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new ApiError(
        data.error || data.description || 'Request failed', 
        res.status
      );
    }
    
    return data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError('Network error. Please try again later.', 0);
  }
}

/**
 * Logs in a user with the provided email/password.
 * Returns the server response (e.g. { message, user }).
 * @param {Object} credentials - User credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @param {boolean} [credentials.rememberMe=false] - Whether to extend session lifetime
 */
export async function loginUser({ email, password, rememberMe = false }) {
  return postJson('/api/auth/login', { email, password, rememberMe });
}

/**
 * Registers a user with the provided email/password.
 * Returns the server response (e.g. { message }).
 * @param {Object} userData - User registration data
 * @param {string} userData.email - User email
 * @param {string} userData.password - User password
 */
export async function registerUser({ email, password }) {
  return postJson('/api/auth/register', { email, password });
}

/** Verifies an email with the given token (GET) */
export async function verifyEmail(token) {
  return getJson(`/api/auth/verify_email/${token}`);
}

/**
 * Request a password reset for a given email
 * @param {string} email - User email
 */
export async function requestPasswordReset(email) {
  return postJson('/api/auth/request_reset', { email });
}

/**
 * Reset password with token and new password
 * @param {string} token - Reset token
 * @param {string} newPassword - New password
 */
export async function resetPassword(token, newPassword) {
  return postJson('/api/auth/reset_password', { token, newPassword });
}

/**
 * Check authentication status
 * Returns user object if logged in
 */
export async function checkAuthStatus() {
  return getJson('/api/auth/status');
}

/**
 * Logout the current user
 */
export async function logoutUser() {
  return postJson('/api/auth/logout', {});
}